import axios from 'axios'
import { vuexOidcCreateUserManager } from 'vuex-oidc'
import store from '@/store'
import { oidcSettings } from '@/config/oidc'
import logger from '@/plugins/logger'

const api = axios.create({
  baseURL: import.meta.env.VITE_BAHN_STATS_BASE_URL,
})

// The token comes from oidc-client-ts's own storage, not from the store: once the access token
// expires, vuex-oidc drops it from the store, and on the next navigation (every route is public)
// only *starts* a silent renew without waiting for it. A page's first request then went out
// anonymously, and history beyond the anonymous window came back 400 until a reload. A tab in the
// background or a sleeping laptop misses automaticSilentRenew's timer, so this is the normal case
// after a longer pause, not an edge case.
//
// Read-only use: automaticSilentRenew is off (oidc-client-ts defaults it on), or this instance
// would run a second renew timer beside vuex-oidc's.
const userManager = vuexOidcCreateUserManager({ ...oidcSettings, automaticSilentRenew: false })

// Renew a little ahead of expiry, so the token does not run out between here and the backend.
const EXPIRY_MARGIN_SECONDS = 30

// Through the store module, so vuex-oidc's state gets the new token too. Concurrent renewals —
// several requests of one page, vuex-oidc's own — join one refresh (plugins/oidc-single-flight.js).
// ignoreErrors makes a failed refresh resolve to null: the request then goes out anonymously, as it
// would have before.
const renewSilently = () => store.dispatch('oidcStore/authenticateOidcSilent', { ignoreErrors: true })

const accessToken = async () => {
  let user
  try {
    user = await userManager.getUser()
  } catch {
    return null
  }
  if (!user) {
    return null
  }
  if (user.expires_in === undefined || user.expires_in > EXPIRY_MARGIN_SECONDS) {
    return user.access_token
  }
  // Without a refresh token the only way back is a full sign-in, which is the user's call.
  if (!user.refresh_token) {
    return null
  }
  const renewed = await renewSilently()
  return renewed?.access_token ?? null
}

api.interceptors.request.use(
  async (config) => {
    const token = await accessToken()

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    return config
  },

  (error) => {
    return Promise.reject(error)
  }
)

// Notification requests carry the browser's push endpoint in their body — a
// secret that must not reach any log, not even the dev console.
const isNotificationRequest = (config) => (config.url || '').includes('notifications/')

api.interceptors.request.use((request) => {
  logger.debug('Request:', isNotificationRequest(request) ? request.url : request)
  return request
})

api.interceptors.response.use((response) => {
  logger.debug('Response:', isNotificationRequest(response.config || {}) ? response.status : response)
  return response
})

export default api

export const apiPlugin = {
  install(app) {
    app.config.globalProperties.$api = api
  },
}
